import React, { useId, useState } from "react";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";

type ReusableCheckboxProps = {
    label?: string;
    initialChecked?: boolean;
    onChange?: (checked: boolean) => void;
    /**
     * Controlled mode. When provided, the atom renders this value instead of its own
     * state, so a parent that re-keys its data (e.g. the composer's knob panel switching
     * registry entries) actually re-renders the box. Omit it for the original
     * uncontrolled `initialChecked` behaviour.
     */
    checked?: boolean;
    /** Replaces the default wrapper class so callers can opt out of the centred layout. */
    className?: string;
    /** Associates the rendered `<label>` with the checkbox's own input — defaults to a generated id so callers that don't need to reference it externally still get an accessible name. */
    inputId?: string;
};

const PrimeCheckbox: React.FC<ReusableCheckboxProps> = ({
    label,
    initialChecked = false,
    onChange,
    checked,
    className = "card flex justify-content-center",
    inputId,
}) => {
    const generatedId = useId();
    const resolvedInputId = inputId ?? generatedId;
    const [internalChecked, setInternalChecked] = useState<boolean>(initialChecked);
    const isControlled = checked !== undefined;
    const value = isControlled ? checked : internalChecked;

    const handleChange = (e: CheckboxChangeEvent) => {
        const next = e.checked ?? false;
        if (!isControlled) {
            setInternalChecked(next);
        }
        if (onChange) {
            onChange(next);
        }
    };

    return (
        <div className={className}>
            <Checkbox inputId={resolvedInputId} onChange={handleChange} checked={value}></Checkbox>
            {label && <label htmlFor={resolvedInputId} style={{ marginLeft: '8px' }}>{label}</label>}
        </div>
    );
};

export default PrimeCheckbox;
